import React, { useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Modal from 'react-modal';
import './Postes.css';
import logo from './logoBranca.png';
import useSerialGenerator from './useSerialGenerator';
import useSerialHistory from './useSerialHistory';
import { FadeIn, AnimatedButton, Particles } from './components/magicui';
import { LogoutIcon, StatsIcon, AdminIcon } from './components/PastelGlyphIcons';
import logger from './utils/logger';
import { sanitizeRegional, validateNumber } from './utils/sanitize';

Modal.setAppElement('#root');

/**
 * Componente para gerar números de série.
 * @param {Object} props - Propriedades do componente.
 * @param {string} props.userRole - Papel do usuário.
 * @param {Array} props.userRegionals - Regionais disponíveis para o usuário.
 */
function Postes({ userRole, userRegionals, onLogout }) {
    const navigate = useNavigate();
    const [selectedRegional, setSelectedRegional] = useState(''); // Estado para a regional selecionada
    const [quantity, setQuantity] = useState(1); // Estado para a quantidade de números de série
    const [userId, setUserId] = useState(null); // Estado para o ID do usuário
    const [modalIsOpen, setModalIsOpen] = useState(false); // Estado para controlar a abertura do modal
    const [showHistory] = useState(false); // Estado para mostrar/ocultar histórico
    const [messageType, setMessageType] = useState('success'); // Estado para controlar o tipo da mensagem (success/error)
    const { generatedSeries, setGeneratedSeries, loading, message, setMessage, generateSeries } = useSerialGenerator(userId); // Usa o hook personalizado
    const { history, loading: historyLoading, error: historyError, exportHistory } = useSerialHistory(userId, userRole, userRegionals); // Hook de histórico

    // Função memorizada para buscar o ID do usuário autenticado
    const fetchUserId = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            setUserId(user.id); // Armazena o ID do usuário
        } else {
            logger.error('Usuário não autenticado');
        }
    }, []);

    // UseEffect que depende de fetchUserId
    useEffect(() => {
        fetchUserId();
    }, [fetchUserId]);

    // Função que salva as séries no Supabase
    const saveSeriesToSupabase = useCallback(async (series, userId) => {
        logger.debug('Salvando séries no banco de dados');

        const { error } = await supabase
            .from('serial_numbers')
            .insert(series.map(serial_number => ({
                serial_number,
                user_id: userId,
                created_at: new Date().toISOString()
            })));

        if (error) {
            logger.error('Erro ao salvar no Supabase:', error.message);
            setMessage('Erro ao salvar os números de série. Tente novamente mais tarde.'); // Mensagem genérica
        } else {
            logger.debug('Números de série salvos com sucesso');
        }
    }, [setMessage]); // Adiciona setMessage como dependência

    /**
     * Manipula a submissão do formulário para gerar números de série.
     * @param {Event} e - Evento de submissão.
     */
    const handleGenerateSeries = async (e) => {
        e.preventDefault(); // Previne o comportamento padrão do formulário

        // Validar e sanitizar entradas
        const sanitizedRegional = sanitizeRegional(selectedRegional);
        const validatedQuantity = validateNumber(quantity, 1, 1000);

        if (!sanitizedRegional) {
            setMessage('Regional inválida selecionada.');
            return;
        }

        if (!validatedQuantity) {
            setMessage('Quantidade deve ser entre 1 e 1000.');
            return;
        }

        await generateSeries(sanitizedRegional, validatedQuantity); // Chama a função para gerar séries
    };

    // UseEffect para abrir o modal quando as séries são geradas
    useEffect(() => {
        if (generatedSeries.length > 0 && !loading) {
            setModalIsOpen(true);
        }
    }, [generatedSeries, loading]);

    // UseEffect para detectar automaticamente o tipo de mensagem
    useEffect(() => {
        if (message) {
            // Palavras-chave que indicam mensagens de erro
            const errorKeywords = ['erro', 'máximo', 'cancelada', 'não foram salvos', 'tente novamente', 'falha'];
            const isError = errorKeywords.some(keyword =>
                message.toLowerCase().includes(keyword.toLowerCase())
            );

            setMessageType(isError ? 'error' : 'success');
        }
    }, [message]);

    const handleConfirm = async () => {
        // Adiciona a caixa de alerta
        const confirmAlert = window.confirm("Após gerar o NS, não poderá mais ser excluído. Se você gerou por acidente, entre em contato com a T.I para solicitar a exclusão dos NS.");
        if (confirmAlert) {
            const csvSaved = await generateCSV(generatedSeries, userId); // Adicionado aqui
            if (csvSaved) {
                setMessage('Números de série gerados e exportados com sucesso!');
                logger.debug("Usuário confirmou a geração dos NS");
            } else {
                setMessage('Erro ao salvar o arquivo CSV. Operação cancelada.');
            }
        } else {
            // O usuário cancelou
            logger.debug("Usuário cancelou a geração dos NS");
        }
        setModalIsOpen(false);
        setGeneratedSeries([]); // Limpa as séries geradas
        resetForm();
    };

    const handleCancel = () => {
        setMessage('Operação cancelada. Números não foram salvos.');
        setModalIsOpen(false);
        setGeneratedSeries([]); // Limpa as séries geradas
        resetForm();
    };

    const resetForm = () => {
        // Remover a linha que redefine a quantidade
        // setQuantity(1); 
        // Não redefinimos a regional também
    };

    const generateCSV = async (series, userId) => {
        try {
            const csvContent = series.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, 'series.csv');
            await saveSeriesToSupabase(series, userId); // Isso agora vai usar a versão memoizada
            return true;
        } catch (error) {
            setMessage('Erro ao gerar o arquivo. Tente novamente mais tarde.'); // Mensagem genérica
            return false;
        }
    };

    // Função para lidar com exportação do histórico
    const handleExportHistory = () => {
        const result = exportHistory();
        setMessage(result.message);
    };


    const allRegionals = [
        { value: 'CR', label: 'CR - Caruaru' },
        { value: 'GR', label: 'GR - Garanhuns' },
        { value: 'PT', label: 'PT - Petrolina' },
        { value: 'ST', label: 'ST - Serra Talhada' },
        { value: 'AV', label: 'AV - Arcoverde' },
        { value: 'RF', label: 'RF - Recife' },
        { value: 'OR', label: 'OR - Ouricuri' },
    ];

    const availableRegionals = userRole === 'master' ? allRegionals : allRegionals.filter(regional => userRegionals.includes(regional.value));

    return (
        <div className="postes-page">
            <Particles className="absolute inset-0 z-0" quantity={20} color="#ffffff" size={0.6} speed={0.3} />

            <div className={`postes-container ${showHistory ? 'expanded' : ''}`} role="main">
                <FadeIn delay={0} className="postes-header">
                    <img src={logo} alt="Logo" className="postes-logo relative z-10" />
                    <h1 className="postes-title">
                        Gerador de NS
                    </h1>
                    <FadeIn delay={1} className="postes-subtitle">
                        Sistema de Numeração de Postes
                    </FadeIn>
                </FadeIn>
                <FadeIn delay={2}>
                <form onSubmit={handleGenerateSeries} className="postes-form" aria-labelledby="form-title">
                    <div className="form-group">
                        <label htmlFor="regional" className="form-label">Selecione a Regional</label>
                        <select
                            id="regional"
                            value={selectedRegional}
                            onChange={(e) => setSelectedRegional(e.target.value)}
                            required
                            className="postes-regional-input"
                            aria-required="true"
                        >
                            <option value="" disabled>Escolha sua regional...</option>
                            {availableRegionals.map((regional) => (
                                <option key={regional.value} value={regional.value}>
                                    {regional.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="quantity" className="form-label">Quantidade de NS</label>
                        <input
                            id="quantity"
                            type="number"
                            min="1"
                            max="1000"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value))}
                            required
                            className="postes-quantity-input"
                            placeholder="Digite a quantidade..."
                            aria-required="true"
                        />
                    </div>

                    <AnimatedButton type="submit" className="postes-button" disabled={loading} aria-live="polite">
                        {loading ? '⚡ Gerando...' : '🚀 Gerar e Exportar'}
                    </AnimatedButton>
                </form>
                </FadeIn>

                {loading && <div className="spinner" role="status" aria-live="polite"></div>}

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={handleCancel}
                contentLabel="Confirmação de Números de Série"
                className="modal"
                overlayClassName="modal-overlay"
                ariaHideApp={false} // Para acessibilidade, se necessário
            >
                <h2 className="modal-title" id="modal-title">Números de Séries Gerados</h2>
                <div className="modal-content" role="document">
                    <ul>
                        {generatedSeries.map((series, index) => (
                            <li key={index}>{series}</li>
                        ))}
                    </ul>
                    <p>Esses são os NS gerados. Você deseja utilizá-los?</p>
                </div>
                <div className="modal-actions">
                    <button type="button" onClick={handleConfirm} className="modal-button">Sim, utilizar esses NS</button>
                    <button type="button" onClick={handleCancel} className="modal-button">Não, cancelar</button>
                </div>
            </Modal>

            {/* Seção do Histórico */}
            <FadeIn delay={3} className="history-section">
                <div className="history-main-button">
                    <AnimatedButton
                        type="button"
                        onClick={() => navigate('/historico')}
                        className="history-page-button"
                    >
                        <StatsIcon size={18} style={{marginRight: '8px'}} />
                        Visualizar Histórico
                    </AnimatedButton>
                </div>

                {/* Botão de Administração (apenas para Master) */}
                {userRole === 'master' && (
                    <div className="admin-main-button">
                            <AnimatedButton
                                type="button"
                                onClick={() => navigate('/admin')}
                                className="admin-page-button"
                            >
                                <AdminIcon size={18} style={{marginRight: '8px'}} />
                                Administração
                            </AnimatedButton>
                    </div>
                )}
            </FadeIn>

                <div className="logout-container">
                    <button
                        type="button"
                        onClick={onLogout}
                        className="logout-button"
                    >
                        <LogoutIcon size={18} style={{marginRight: '8px'}} />
                        Sair
                    </button>
                </div>

                {showHistory && history.length > 0 && (
                    <button
                        type="button"
                        onClick={handleExportHistory}
                        className="history-export-button"
                        disabled={historyLoading}
                    >
                        Exportar Prévia
                    </button>
                )}

                {showHistory && (
                    <div className="history-content">
                        {historyLoading && <div className="spinner" role="status">Carregando histórico...</div>}

                        {historyError && <p className="error-message">{historyError}</p>}

                        {!historyLoading && !historyError && (
                            <>
                                <h3>Histórico de Números de Série</h3>
                                <p className="history-info">
                                    Total: {history.length} números{' '}
                                    {userRole !== 'master' && (
                                        <span>(suas regionais: {userRegionals.join(', ')})</span>
                                    )}
                                </p>

                                {history.length === 0 ? (
                                    <p className="no-history">Nenhum número de série encontrado.</p>
                                ) : (
                                    <div className="history-table-container">
                                        <table className="history-table">
                                            <thead>
                                                <tr>
                                                    <th>Número de Série</th>
                                                    <th>Data de Criação</th>
                                                    <th>Usuário</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {history.slice(0, 100).map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{item.serial_number}</td>
                                                        <td>{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                                                        <td>{item.profiles?.email || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {history.length > 100 && (
                                            <p className="history-note">
                                                Mostrando os 100 registros mais recentes.
                                                Use "Exportar Histórico" para ver todos os {history.length} registros.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {message && <p className={`postes-message ${messageType === 'error' ? 'error-message' : ''}`} role="alert">{message}</p>}
            </div>
        </div>
    );
}

export default Postes;
